import axiosClient from './axios-client'

export type GeneratedSiteStatus = 'PENDING' | 'QUOTA_DEFERRED' | 'GENERATING' | 'READY' | 'FAILED' | 'DELETED'

// domaine client inclus (specs/user-generated-sites/hebergement.md §6.6) : les quatre premiers statuts sont « en cours »
export type DomainStatus = 'RESERVING' | 'REGISTERED' | 'DNS_CONFIGURED' | 'CERT_PENDING' | 'ACTIVE' | 'FAILED'

export type SiteEdit = {
  id: number
  instruction: string
  inputMode: 'voice' | 'text'
  creditsCost: number
  status: 'PENDING' | 'GENERATING' | 'DONE' | 'FAILED'
  refused: boolean
  createdAt: string
}

// grille de prix de l'A/B test, tirée côté serveur à la création du site (`site.priceVariant`) :
// A = grille d'origine, B = grille à 9,90 € HT/mois
export type SitePriceVariant = 'A' | 'B'

export type GeneratedSite = {
  id: number
  status: GeneratedSiteStatus
  generationRefused: boolean
  commercialBrand: string | null
  categoryLabel: string | null
  tagline: string | null
  previewUrl: string | null
  htmlVersion: number | null
  thumbnailUrl: string | null
  interest: boolean
  viewedAt: string | null
  expiresAt: string | null
  generationStartedAt: string | null
  createdAt: string
  plan: string | null
  priceVariant: SitePriceVariant | null
  purchasedAt: string | null
  // essai gratuit en cours : date du premier prélèvement, null sinon
  trialEndsAt: string | null
  planExpiresAt: string | null
  // page publique partageable du site (/votre-site/<token>), null tant que le serveur n'a pas posé le jeton
  publicUrl: string | null
  // « Je veux une publicité » déjà demandé
  adInterestAt: string | null
  // publicités prêtes à diffuser, générées avec le site (null tant qu'elles n'existent pas)
  ads: SiteAds | null
  publishedUrl: string | null
  // site retiré de la ligne (fin d'abonnement ou retrait par l'équipe) : ses adresses répondent 410
  unpublishedAt: string | null
  // pourquoi : essai arrêté ou abonnement fini (réactivable depuis l'espace client), retrait
  // manuel par l'équipe
  unpublishReason: 'trial_canceled' | 'subscription_ended' | 'manual' | null
  creditsBalance: number
  creditsUsed: number
  editCost: number
  freeEditsRemaining: number
  isUpdating: boolean
  edits: SiteEdit[]
  paymentLast4: string | null
  offerAvailable: boolean
  siteUrl: string | null
  domain: string | null
  domainStatus: DomainStatus | null
  domainOfferAvailable: boolean
  // le titulaire n'a pas encore cliqué l'email de vérification ICANN envoyé par le registrar
  domainVerificationPending: boolean
}

export type DomainCheck = {
  domain: string
  available: boolean
  included: boolean
  priceCents: number | null
  currency: string
  reason: string | null
  allowedTlds?: string[]
}

// un site d'avant le test (priceVariant null) est en grille A, comme côté serveur
export const sitePriceVariant = (site: Pick<GeneratedSite, 'priceVariant'>): SitePriceVariant => site.priceVariant ?? 'A'

export type SitePlan = {
  key: 'month' | 'year1' | 'year2' | 'year3'
  priceVariant: SitePriceVariant
  label: string
  shortLabel: string
  badge: string | null
  amountHt: number
  amountTtc: number
  monthlyEquivalentHt: number
  discountPercent: number
  credits: number
  months: number
  // jours d'essai gratuit (plan mensuel seulement, 0 sinon)
  trialDays: number
}

export type CreditPack = {
  key: 'pack10' | 'pack30' | 'pack100'
  credits: number
  amountHt: number
  amountTtc: number
  unitPriceHt: number
}

export type SiteOffers = {
  priceVariant: SitePriceVariant
  plans: SitePlan[]
  packs: CreditPack[]
  editCost: number
  freePreviewEdits: number
  trialDays: number
}

export const getMyGeneratedSite = () => axiosClient.get<GeneratedSite>('/api/generated-site/mine')

export const getSiteOffers = (variant: SitePriceVariant) => axiosClient
  .get<SiteOffers>('/api/generated-site/offers', { params: { variant } })

export const markGeneratedSiteViewed = () => axiosClient.post<GeneratedSite>('/api/generated-site/mine/viewed')

export const markGeneratedSiteInterest = () => axiosClient.post<GeneratedSite>('/api/generated-site/mine/interest')

export type SubscribeToSitePayload = { plan: SitePlan['key'] }

export const subscribeToSite = (payload: SubscribeToSitePayload) => axiosClient
  .post<{ site: GeneratedSite, publishedUrl: string | null }>('/api/generated-site/mine/subscribe', payload)

export const cancelSiteSubscription = () => axiosClient
  .post<{ site: GeneratedSite, canceled: boolean, immediate: boolean, endsAt: string | null }>('/api/generated-site/mine/cancel')

export const markSiteAdInterest = () => axiosClient.post<GeneratedSite>('/api/generated-site/mine/ad-interest')

export const buySiteCredits = (pack: CreditPack['key']) => axiosClient.post<{ site: GeneratedSite, creditsBalance: number }>('/api/generated-site/mine/credits', { pack })

export const checkSiteDomain = (domain: string) => axiosClient.get<DomainCheck>('/api/generated-site/domains/check', { params: { domain } })

export const reserveSiteDomain = (domain: string) => axiosClient.post<{ site: GeneratedSite, domain: string, status: DomainStatus }>('/api/generated-site/mine/domain', { domain })

export const resendDomainVerificationEmail = () => axiosClient.post<{ sent: boolean }>('/api/generated-site/mine/domain/verification-email')

export const requestSiteEdit = (payload: { instruction: string, inputMode: 'voice' | 'text' }) => axiosClient.post<{ edit: SiteEdit, cost: number, deferred: boolean }>('/api/generated-site/mine/edits', payload)

export type TranscriptionResponse = { text: string, duration: number }

const POLL_INTERVAL_MS = 1000
const POLL_MAX_MS = 90000

const sleep = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms) })

// le serveur répond 200 avec le texte, ou 202 { jobId } quand le service de transcription met plus de ~20 s.
// `subscriptionToken` est vide pour un client connecté (la session suffit côté serveur).
export const transcribeVoiceNote = async (audio: Blob, subscriptionToken: string | null): Promise<TranscriptionResponse> => {
  const formData = new FormData()
  const extension = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm'
  formData.append('audio', audio, `voice-note.${extension}`)
  if (subscriptionToken) formData.append('subscriptionToken', subscriptionToken)

  const first = await axiosClient.post<TranscriptionResponse & { jobId?: string }>('/api/subscription/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })

  if (first.status !== 202 || !first.data.jobId) return first.data

  const deadline = Date.now() + POLL_MAX_MS
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS)
    const poll = await axiosClient.get<{ status: string, text?: string, duration?: number }>(`/api/subscription/transcribe/${first.data.jobId}`)
    if (poll.status === 200 && poll.data.status === 'done') {
      return { text: poll.data.text || '', duration: poll.data.duration || 0 }
    }
  }

  throw new Error('transcription timeout')
}

export const formatEuros = (cents: number, { decimals = 2 }: { decimals?: number } = {}) => `${(cents / 100).toFixed(decimals).replace('.', ',')} €`

// aperçu d'un lead avant le paiement, page publique /inscription/votre-site?token=<uuid> (lien de la relance,
// hebergement.md §9.4 mesure C) : jamais de session, le jeton d'inscription fait office de secret
export type LeadGeneratedSite = { paid: true } | {
  paid: false
  status: GeneratedSiteStatus
  commercialBrand: string | null
  previewUrl: string | null
  htmlVersion: number | null
}

export const getLeadGeneratedSite = (token: string) => axiosClient
  .get<LeadGeneratedSite>(`/api/generated-site/lead/${encodeURIComponent(token)}`)

// page publique partageable du site d'un client, /votre-site/<token> (cible des emails de relance : le client voit son
// site et les arguments sans se connecter, la connexion n'arrive qu'au clic « Garder mon site »)
export type SiteAds = {
  feedUrl: string
  storyUrl: string
  headline: string | null
  cta: string | null
  generatedAt: string | null
}

export type PublicGeneratedSite = {
  commercialBrand: string | null
  tagline: string | null
  status: GeneratedSiteStatus
  previewUrl: string | null
  htmlVersion: number | null
  purchased: boolean
  unpublished: boolean
  expiresAt: string | null
  priceVariant: SitePriceVariant
  monthlyAmountHt: number
  trialDays: number
  // page de vente : secteur et description du site, nombre de sites déjà en ligne (preuve sociale)
  categoryLabel?: string | null
  siteDescription?: string | null
  serviceArea?: string | null
  sitesOnline?: number
  ads?: SiteAds | null
}

export const getPublicGeneratedSite = (token: string) => axiosClient
  .get<PublicGeneratedSite>(`/api/generated-site/public/${encodeURIComponent(token)}`)
